import { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, ListChecks, Bell, CheckCheck, Zap } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { Notification } from './useNotifications';

interface AlertsDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
}

function getNotificationIcon(type: string) {
  const style = { color: 'var(--text-faint)' };
  switch (type) {
    case 'mention':
      return <MessageSquare className="w-4 h-4 flex-shrink-0" style={style} />;
    case 'task_assigned':
    case 'task_status':
      return <ListChecks className="w-4 h-4 flex-shrink-0" style={style} />;
    case 'agent_queue':
      return <Zap className="w-4 h-4 flex-shrink-0" style={style} />;
    default:
      return <Bell className="w-4 h-4 flex-shrink-0" style={style} />;
  }
}

export default function AlertsDropdown({
  isOpen,
  onClose,
  notifications,
  unreadCount,
  loading,
  markAsRead,
  markAllAsRead,
}: AlertsDropdownProps) {
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    // Use a timeout to avoid capturing the click that opened the dropdown
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    if (notification.link) {
      navigate(notification.link);
    }
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={dropdownRef}
          initial={{ opacity: 0, y: -4, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -4, scale: 0.98 }}
          transition={{ duration: 0.15 }}
          className="absolute top-full right-0 mt-2 w-[380px] overflow-hidden z-50"
          style={{
            background: 'var(--surface-card)',
            border: '1px solid var(--border-hairline)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-pop)',
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{ borderBottom: '1px solid var(--border-hairline)', background: 'var(--surface-raised)' }}
          >
            <h3 className="t-eyebrow">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="flex items-center gap-1.5 text-xs font-medium hover-underline"
                style={{ color: 'var(--accent-text)' }}
              >
                <CheckCheck className="w-3.5 h-3.5" />
                Mark All as Read
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-[400px] overflow-y-auto chat-scrollbar">
            {loading ? (
              <div className="px-4 py-8 text-center">
                <p className="text-sm" style={{ color: 'var(--text-faint)' }}>Loading...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <Bell className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--text-faint)', opacity: 0.5 }} />
                <p className="text-sm" style={{ color: 'var(--text-faint)' }}>No notifications yet</p>
              </div>
            ) : (
              notifications.map(notification => (
                <button
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className="w-full text-left px-4 py-3 flex items-start gap-3 hover-wash"
                  style={{
                    borderBottom: '1px solid var(--border-hairline)',
                    background: !notification.read ? 'var(--accent-soft)' : 'transparent',
                  }}
                >
                  {/* Unread dot */}
                  <div className="flex-shrink-0 pt-1 w-2">
                    {!notification.read && (
                      <div className="w-2 h-2 rounded-full" style={{ background: 'var(--accent)' }} />
                    )}
                  </div>

                  {/* Icon */}
                  <div className="flex-shrink-0 pt-0.5">
                    {getNotificationIcon(notification.type)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p
                      className={`text-xs leading-relaxed ${!notification.read ? 'font-semibold' : 'font-medium'}`}
                      style={{ color: 'var(--text-body)' }}
                    >
                      {notification.title}
                    </p>
                    {notification.body && (
                      <p className="text-xs leading-relaxed mt-0.5 line-clamp-2" style={{ color: 'var(--text-muted)' }}>
                        {notification.body}
                      </p>
                    )}
                    <p className="t-num text-[10px] mt-1" style={{ color: 'var(--text-faint)' }}>
                      {formatDistanceToNow(new Date(notification.created_at), {
                        addSuffix: true,
                      })}
                    </p>
                  </div>
                </button>
              ))
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
